<template>
  <div class="flex flex-1">
    <div class="space-y-3 flex-1 min-w-0 max-w-100 overflow-scroll">
      <uiList
        :items="codes"
        :search="['code', 'notes']"
        :add="true"
        width="auto"
        @add="setNewForm"
      >
        <template #items="{ items: codes }">
          <div class="space-y-3 flex-1 min-w-0 px-5 pt-2 pb-6 overflow-scroll">
            <div
              v-for="code in codes"
              :key="code.id"
              class="cursor-pointer space-y-1 rounded-2xl p-3"
              :class="{
                'bg-light-500': selected?.id !== code.id,
                'bg-white shadow-lg': selected?.id === code.id
              }"
              @click="handleSelectCode(code)"
            >
              <div class="flex items-center">
                <div class="flex space-x-1.5 items-center">
                  <div
                    class="rounded-full h-1.3 w-1.3"
                    :class="{
                      'bg-red-500': !code.active,
                      'bg-green-500': code.active
                    }"
                  />
                  <p class="font-semibold m-0 text-base whitespace-nowrap">
                    {{ code.code }}
                  </p>
                </div>

                <p class="ml-auto m-0 text-xs text-zinc-500">
                  Активен до:
                  <span
                    class="font-medium"
                    :class="{
                      'text-red-500': isCodeExpired(code.activeUntil)
                    }"
                  >
                    {{ format(new Date(code.activeUntil), 'dd.MM.yyyy') }}
                  </span>
                </p>
              </div>

              <p class="font-medium m-0 text-xs opacity-50">
                {{ daysCountText(code.days) }}
              </p>

              <p class="font-medium m-0 text-xs opacity-50">
                {{ appliedCountText(code.appliedCount) }}
              </p>

              <p
                v-if="code.notes"
                class="m-0 text-sm opacity-50 whitespace-pre-line"
              >
                {{ code.notes }}
              </p>
            </div>
          </div>
        </template>
      </uiList>
    </div>

    <div class="flex-1 min-w-0 max-w-110 p-8">
      <div v-if="selected != null" class="space-y-3">
        <div class="flex space-x-3">
          <uiInput
            v-model="selected.code"
            label="Промокод"
            placeholder="Например, NEWYEAR2024"
            uppercase
          />

          <div>
            <p class="m-0 text-sm mb-1 opacity-50">Активен</p>
            <div class="py-2.5">
              <uiSwitch v-model="selected.active" />
            </div>
          </div>
        </div>

        <div class="flex space-x-3">
          <uiDropdown
            v-model="selected.kind"
            label="Тип промокода"
            :options="kindList"
            :reduce="item => item.value"
            clearSearchOnSelect
            class="flex-1"
          />

          <div v-if="selected.kind === 'price'" class="flex-1">
            <uiInput v-model="selected.price" label="Стоимость" mask="9999" />
          </div>
        </div>

        <div class="flex space-x-3">
          <uiInput v-model="selected.days" label="Количество дней" mask="999" />

          <div>
            <p class="m-0 text-sm mb-1 opacity-50">Активен до</p>
            <DatePicker
              v-model="selected.activeUntil"
              type="date"
              format="DD.MM.YYYY"
            />
          </div>
        </div>

        <uiTextarea v-model="selected.notes" label="Заметки" />

        <div class="flex py-3 justify-between">
          <div>
            <uiButton
              v-if="selected?.id"
              class="px-5"
              color="red"
              :loading="remove.isPending"
              @click="remove()"
            >
              Удалить
            </uiButton>
          </div>

          <uiButton
            class="px-5"
            :loading="save.isPending"
            @click="save()"
            :disabled="!canSave"
          >
            {{ selected?.id ? 'Сохранить' : 'Создать' }}
          </uiButton>
        </div>

        <div
          v-if="selected?.appliedCount > 0"
          class="border-solid border-t-[#eee] border-0 border-t-2 mt-3 pt-6"
        >
          <uiButton
            v-if="users.length === 0"
            class="px-5"
            color="grey"
            :loading="getUsers.isPending"
            @click="getUsers()"
          >
            Загрузить применивших
          </uiButton>

          <div v-else>
            <virtual-list
              :data-key="'_id'"
              :data-sources="users"
              :data-component="VirtualListUser"
              :estimate-size="20"
              class="max-h-50 overflow-auto"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { format, isBefore, add, startOfDay } from 'date-fns'
import { pluralize } from 'numeralize-ru'
import DatePicker from 'vue2-datepicker'
import api from '@/utils/ky'
import { API } from '@/config'
import { useAsyncMethod } from '@/utils/async-methods'
import { confirm, notify } from '@/utils/events'
import { mapToList } from '@/utils/select'
import VirtualListUser from '@/components/VirtualListUser'

const kindMap = {
  days: 'На дни',
  price: 'На стоимость'
}

export default {
  components: {
    DatePicker,
    VirtualListUser
  },
  setup () {
    const codes = ref([])
    function setCodes (newCodes) {
      codes.value = newCodes.map(code => ({
        ...code,
        activeUntil: new Date(code.activeUntil)
      }))
    }

    onMounted(async () => {
      const { data } = await api.get(API.PROMOCODES.list)
      setCodes(data)
    })

    const selected = ref(null)

    const isCodeExpired = activeUntil =>
      isBefore(new Date(activeUntil), new Date())
    const daysCountText = days =>
      `на ${days} ${pluralize(days, 'день', 'дня', 'дней')}`
    const appliedCountText = count => {
      if (count === 0) return 'Никто не применял'
      return `${count === 1 ? 'Применил' : 'Применили'} ${count} ${pluralize(
        count,
        'пользователь',
        'пользователя',
        'пользователей'
      )}`
    }

    function setNewForm () {
      selected.value = {
        code: '',
        kind: 'days',
        active: true,
        activeUntil: add(new Date(), { months: 1 }),
        days: 30,
        price: undefined,
        notes: ''
      }
    }

    const canSave = computed(() => {
      return [
        selected.value.code,
        selected.value.days,
        selected.value.activeUntil,
        selected.value.kind,
        selected.value.kind === 'price' ? selected.value.price > 0 : true
      ].every(Boolean)
    })

    const save = useAsyncMethod(async () => {
      if (!canSave.value) return

      const payload = {
        ...selected.value,
        days: Number(selected.value.days),
        price:
          selected.value.kind === 'price'
            ? Number(selected.value.price) || undefined
            : null,
        activeUntil: startOfDay(selected.value.activeUntil)
      }

      if (selected.value?.id) {
        const { data } = await api.post(API.PROMOCODES.update, payload)
        setCodes(data)
      } else {
        const { data } = await api.post(API.PROMOCODES.create, payload)
        if (data.err) {
          return notify(data.err)
        }

        setCodes(data)
      }

      selected.value = null
    })

    const remove = useAsyncMethod(async () => {
      const confirmed = await confirm('Точно удаляем?')
      if (!confirmed) return

      const { data } = await api.post(API.PROMOCODES.delete, {
        id: selected.value?.id
      })

      setCodes(data)
      selected.value = null
    })

    const users = ref([])
    const getUsers = useAsyncMethod(async () => {
      users.value = []

      const { data } = await api.post(API.PROMOCODES.users, {
        code: selected.value?.code
      })
      users.value = data
    })

    function handleSelectCode (code) {
      selected.value = structuredClone(code)
      users.value = []
    }

    return {
      kindList: mapToList(kindMap),
      codes,
      selected,
      handleSelectCode,
      format,
      isCodeExpired,
      daysCountText,
      appliedCountText,
      setNewForm,
      canSave,
      save,
      remove,

      VirtualListUser,
      users,
      getUsers
    }
  }
}
</script>
